// src/pages/RHAdmin.js
import React, { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { rhAdminService } from '../services/rhAdminService';
import { employeeService } from '../services/employeeService';
import './RHAdmin.css';

const RH_TYPES = [
  { id: 'conge_paye', name: 'Congé payé', icon: '🌴', color: '#10b981' },
  { id: 'fiche_paie', name: 'Fiche de paie', icon: '📄', color: '#3b82f6' },
  { id: 'certificat_medical', name: 'Certificat médical', icon: '🏥', color: '#ef4444' },
  { id: 'avance_salaire', name: 'Avance sur salaire', icon: '💰', color: '#f59e0b' },
  { id: 'attestation_travail', name: 'Attestation de travail', icon: '📋', color: '#8b5cf6' },
  { id: 'reclamation', name: 'Réclamation', icon: '📝', color: '#6b7280' },
];

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: '#f59e0b', bg: '#fef3c7', icon: '⏳' },
  processing: { label: 'En traitement', color: '#3b82f6', bg: '#dbeafe', icon: '🔄' },
  approved: { label: 'Approuvé', color: '#10b981', bg: '#d1fae5', icon: '✅' },
  rejected: { label: 'Refusé', color: '#ef4444', bg: '#fee2e2', icon: '❌' },
  cancelled: { label: 'Annulé', color: '#6b7280', bg: '#f3f4f6', icon: '🚫' }
};

const RHAdmin = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    approved: 0,
    rejected: 0,
    byType: {}
  });

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const filters = {};
      
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (typeFilter !== 'all') filters.type = typeFilter;
      if (employeeFilter !== 'all') filters.employeeId = employeeFilter;
      
      console.log('Filtres appliqués:', filters);
      
      const data = await rhAdminService.getRequests(filters);
      setRequests(data || []);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const data = await employeeService.getEmployees();
      setEmployees(data.employees || []);
    } catch (err) {
      console.error('Erreur chargement employés:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await rhAdminService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Erreur chargement stats:', err);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchEmployees();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [statusFilter, typeFilter, employeeFilter]);

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setResponseMessage('');
    setSelectedFile(null);
    setShowResponseModal(true);
  };

  const handleReject = async (request) => {
    const message = prompt('Message de refus (optionnel) :');
    if (!window.confirm(`Refuser la demande de ${request.user?.firstName} ${request.user?.lastName} ?`)) return;
    try {
      await rhAdminService.rejectRequest(request._id, message || 'Demande refusée');
      await fetchRequests();
      await fetchStats();
      alert('✅ Demande refusée');
    } catch (err) {
      setError(err.message || 'Erreur lors du refus');
    }
  };

  const handleMarkAsProcessing = async (request) => {
    try {
      await rhAdminService.markAsProcessing(request._id);
      await fetchRequests();
      await fetchStats();
      alert('✅ Demande marquée en traitement');
    } catch (err) {
      setError(err.message || 'Erreur lors du marquage');
    }
  };

  const submitResponse = async () => {
    if (!selectedRequest) return;
    
    try {
      await rhAdminService.approveRequest(selectedRequest._id, responseMessage, selectedFile);
      setShowResponseModal(false);
      setSelectedRequest(null);
      await fetchRequests();
      await fetchStats();
      alert('✅ Demande approuvée');
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'approbation');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'application/pdf' || file.type.startsWith('image/'))) {
      setSelectedFile(file);
    } else {
      alert('Format non supporté. Utilisez PDF ou image.');
    }
  };

  const getTypeInfo = (type) => {
    return RH_TYPES.find(t => t.id === type) || RH_TYPES[0];
  };

  const getStatusInfo = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusActions = (request) => {
    switch(request.status) {
      case 'pending':
        return (
          <div className="actions-cell">
            <button className="action-btn processing" onClick={() => handleMarkAsProcessing(request)} title="Marquer en traitement">
              🔄
            </button>
            <button className="action-btn approve" onClick={() => handleApprove(request)} title="Approuver">
              ✅
            </button>
            <button className="action-btn reject" onClick={() => handleReject(request)} title="Refuser">
              ❌
            </button>
          </div>
        );
      case 'processing':
        return (
          <div className="actions-cell">
            <button className="action-btn approve" onClick={() => handleApprove(request)} title="Approuver">
              ✅
            </button>
            <button className="action-btn reject" onClick={() => handleReject(request)} title="Refuser">
              ❌
            </button>
          </div>
        );
      default:
        return <span className="action-disabled">-</span>;
    }
  };

  if (loading && requests.length === 0) {
    return (
      <div className="rh-admin">
        <div className="page-header">
          <div className="page-title">
            <h1>Gestion RH</h1>
          </div>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rh-admin">
      <div className="page-header">
        <div className="page-title">
          <h1>Gestion des Ressources Humaines</h1>
          <p>Gérez les demandes de vos employés ({requests.length} demande{requests.length > 1 ? 's' : ''})</p>
        </div>
        
        <div className="header-actions">
          <div className="filters">
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">📋 Tous les statuts</option>
              <option value="pending">⏳ En attente</option>
              <option value="processing">🔄 En traitement</option>
              <option value="approved">✅ Approuvées</option>
              <option value="rejected">❌ Refusées</option>
            </select>

            <select 
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">🎁 Tous les types</option>
              {RH_TYPES.map(type => (
                <option key={type.id} value={type.id}>
                  {type.icon} {type.name}
                </option>
              ))}
            </select>

            <select 
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">👥 Tous les employés</option>
              {employees.map(emp => (
                <option key={emp._id} value={emp._id}>
                  {emp.firstName} {emp.lastName}
                </option>
              ))}
            </select>

            {(statusFilter !== 'all' || typeFilter !== 'all' || employeeFilter !== 'all') && (
              <button 
                className="filter-reset"
                onClick={() => {
                  setStatusFilter('all');
                  setTypeFilter('all');
                  setEmployeeFilter('all');
                }}
              >
                🔄 Réinitialiser
              </button>
            )}
          </div>
        </div>
      </div>

      {/* STATISTIQUES */}
      <div className="rh-stats">
        <Card className="stat-card">
          <div className="stat-content">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <div className="stat-number">{stats.total}</div>
              <div className="stat-label">Total demandes</div>
            </div>
          </div>
        </Card>
        <Card className="stat-card pending">
          <div className="stat-content">
            <div className="stat-icon">⏳</div>
            <div className="stat-info">
              <div className="stat-number">{stats.pending}</div>
              <div className="stat-label">En attente</div>
            </div>
          </div>
        </Card>
        <Card className="stat-card processing">
          <div className="stat-content">
            <div className="stat-icon">🔄</div>
            <div className="stat-info">
              <div className="stat-number">{stats.processing}</div>
              <div className="stat-label">En traitement</div>
            </div>
          </div>
        </Card>
        <Card className="stat-card approved">
          <div className="stat-content">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <div className="stat-number">{stats.approved}</div>
              <div className="stat-label">Approuvées</div>
            </div>
          </div>
        </Card>
        <Card className="stat-card rejected">
          <div className="stat-content">
            <div className="stat-icon">❌</div>
            <div className="stat-info">
              <div className="stat-number">{stats.rejected}</div>
              <div className="stat-label">Refusées</div>
            </div>
          </div>
        </Card>
      </div>

      {error && (
        <Card className="error-card" padding="md">
          <div className="error-message">❌ {error}</div>
        </Card>
      )}

      {/* TABLEAU DES DEMANDES */}
      <div className="rh-table-container">
        <table className="rh-table">
          <thead>
            <tr>
              <th>Employé</th>
              <th>Type</th>
              <th>Détails</th>
              <th>Statut</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.map(request => {
              const typeInfo = getTypeInfo(request.type);
              const statusInfo = getStatusInfo(request.status);
              const data = request.data || {};
              
              return (
                <tr key={request._id}>
                  <td data-label="Employé">
                    <div className="employee-cell">
                      <strong>{request.user?.firstName} {request.user?.lastName}</strong>
                      <small>{request.user?.employeeId}</small>
                    </div>
                   </td>
                  <td data-label="Type">
                    <span className="type-badge" style={{ backgroundColor: `${typeInfo.color}15`, color: typeInfo.color }}>
                      {typeInfo.icon} {typeInfo.name}
                    </span>
                   </td>
                  <td data-label="Détails">
                    <div className="details-cell">
                      {data.startDate && data.endDate && (
                        <div className="detail-line">
                          <span className="detail-icon">📅</span>
                          <span>{formatDate(data.startDate)} → {formatDate(data.endDate)}</span>
                        </div>
                      )}
                      {data.amount > 0 && (
                        <div className="detail-line">
                          <span className="detail-icon">💰</span>
                          <span>{data.amount.toLocaleString()} DA</span>
                        </div>
                      )}
                      {data.reason && (
                        <div className="detail-line reason-line" title={data.reason}>
                          <span className="detail-icon">📝</span>
                          <span className="reason-text">{data.reason.length > 50 ? data.reason.substring(0, 50) + '...' : data.reason}</span>
                        </div>
                      )}
                      {data.documentUrl && (
                        <div className="detail-line">
                          <a href={data.documentUrl} target="_blank" rel="noopener noreferrer" className="document-link">📎 Document</a>
                        </div>
                      )}
                    </div>
                    </td>
                  <td data-label="Statut">
                    <span className="status-badge" style={{ backgroundColor: statusInfo.bg, color: statusInfo.color }}>
                      {statusInfo.icon} {statusInfo.label}
                    </span>
                    </td>
                  <td data-label="Date">{formatDate(request.createdAt)}</td>
                  <td data-label="Actions">
                    <div className="actions-cell">
                      {getStatusActions(request)}
                      <button className="action-btn view" onClick={() => setSelectedRequest(request)} title="Voir détails">
                        👁️
                      </button>
                    </div>
                    </td>
                 </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {requests.length === 0 && !loading && (
        <Card className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>Aucune demande RH trouvée</h3>
          <Button variant="primary" onClick={() => {
            setStatusFilter('all');
            setTypeFilter('all');
            setEmployeeFilter('all');
          }}>
            📋 Voir toutes les demandes
          </Button>
        </Card>
      )}

      {/* MODAL DÉTAILS */}
      {selectedRequest && !showResponseModal && (
        <div className="modal-overlay" onClick={() => setSelectedRequest(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Détails de la demande</h2>
              <button className="modal-close" onClick={() => setSelectedRequest(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <label>Employé:</label>
                <span><strong>{selectedRequest.user?.firstName} {selectedRequest.user?.lastName}</strong> ({selectedRequest.user?.employeeId})</span>
              </div>
              <div className="detail-row">
                <label>Email:</label>
                <span>{selectedRequest.user?.email}</span>
              </div>
              <div className="detail-row">
                <label>Type:</label>
                <span>{getTypeInfo(selectedRequest.type)?.icon} {getTypeInfo(selectedRequest.type)?.name}</span>
              </div>
              <div className="detail-row">
                <label>Statut:</label>
                <span style={{ color: getStatusInfo(selectedRequest.status).color }}>
                  {getStatusInfo(selectedRequest.status).icon} {getStatusInfo(selectedRequest.status).label}
                </span>
              </div>
              <div className="detail-row">
                <label>Date:</label>
                <span>{formatDate(selectedRequest.createdAt)}</span>
              </div>
              {selectedRequest.data?.startDate && selectedRequest.data?.endDate && (
                <div className="detail-row">
                  <label>Période:</label>
                  <span>{formatDate(selectedRequest.data.startDate)} → {formatDate(selectedRequest.data.endDate)}</span>
                </div>
              )}
              {selectedRequest.data?.amount > 0 && (
                <div className="detail-row">
                  <label>Montant:</label>
                  <span>{selectedRequest.data.amount.toLocaleString()} DA</span>
                </div>
              )}
              {selectedRequest.data?.reason && (
                <div className="detail-row">
                  <label>Motif:</label>
                  <div className="detail-message">{selectedRequest.data.reason}</div>
                </div>
              )}
              {selectedRequest.data?.documentUrl && (
                <div className="detail-row">
                  <label>Document:</label>
                  <a href={selectedRequest.data.documentUrl} target="_blank" rel="noopener noreferrer" className="document-link">📄 Télécharger</a>
                </div>
              )}
              {selectedRequest.data?.responseMessage && (
                <div className="detail-row">
                  <label>Réponse:</label>
                  <div className="detail-message response-message">{selectedRequest.data.responseMessage}</div>
                </div>
              )}
              {selectedRequest.data?.responseDocumentUrl && (
                <div className="detail-row">
                  <label>Document réponse:</label>
                  <a href={selectedRequest.data.responseDocumentUrl} target="_blank" rel="noopener noreferrer" className="document-link">📎 Télécharger</a>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {(selectedRequest.status === 'pending' || selectedRequest.status === 'processing') && (
                <>
                  <button className="modal-action-btn approve" onClick={() => {
                    setSelectedRequest(null);
                    handleApprove(selectedRequest);
                  }}>✅ Approuver</button>
                  <button className="modal-action-btn reject" onClick={() => {
                    setSelectedRequest(null);
                    handleReject(selectedRequest);
                  }}>❌ Refuser</button>
                </>
              )}
              <button className="modal-action-btn close" onClick={() => setSelectedRequest(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL RÉPONSE (APPROBATION) */}
      {showResponseModal && (
        <div className="modal-overlay" onClick={() => {
          setShowResponseModal(false);
          setSelectedRequest(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✅ Approuver la demande</h2>
              <button className="modal-close" onClick={() => {
                setShowResponseModal(false);
                setSelectedRequest(null);
              }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Message de réponse</label>
                <textarea
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  placeholder="Ajoutez un message pour l'employé..."
                  rows="4"
                />
              </div>
              <div className="form-group">
                <label>Document joint</label>
                <input type="file" onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png" />
                <small>PDF, JPG ou PNG (max 5MB) - Fiche de paie, attestation, etc.</small>
              </div>
              {selectedFile && (
                <div className="file-info">
                  📎 Fichier sélectionné : {selectedFile.name}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-action-btn close" onClick={() => {
                setShowResponseModal(false);
                setSelectedRequest(null);
              }}>Annuler</button>
              <button className="modal-action-btn approve" onClick={submitResponse}>Approuver</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RHAdmin;